import { Autocomplete, TextField, Theme, Box } from '@mui/material';
import { SxProps } from '@mui/system';
import { State } from '@progress/kendo-data-query';
import * as React from 'react';
import { useTransition, useState, useRef } from 'react';
import VendorApi from 'src/api/VendorApi';
import { VendorUser } from 'src/types/vendorUser';

type TypeAheadProps = {
  sx?: SxProps<Theme>;
  onChange: (value: NonNullable<string | VendorUser>) => void;
  style?: React.CSSProperties;
};

const UserTypeAhead: React.FC<React.PropsWithChildren<TypeAheadProps>> = ({ onChange, sx, style }) => {
  const currentSearchString = useRef<string>('');
  const [, startTransition] = useTransition();
  const [users, setUsers] = useState<VendorUser[]>([]);

  const onInputChange = (searchText: string) => {
    currentSearchString.current = searchText;

    if (searchText == null || searchText.length < 2) {
      startTransition(() => {
        setUsers([]);
      });
      return;
    }

    VendorApi.getUsers({
      take: 20,
      filter: {
        logic: 'and',
        value: '',
        filters: [
          {
            filters: [
              { field: 'emailAddress', operator: 'contains', value: searchText },
              { field: 'username', operator: 'contains', value: searchText },
            ],
            logic: 'or',
          },
        ],
      },
    } as State).then((result) => {
      if (searchText === currentSearchString.current) {
        startTransition(() => {
          setUsers(result.value);
        });
      }
    });
  };

  return (
    <Autocomplete
      sx={sx}
      options={users}
      filterOptions={(x) => x}
      multiple={false}
      autoComplete
      style={style}
      onChange={(e, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(e, newValue) => {
        onInputChange(newValue);
      }}
      isOptionEqualToValue={(option, value) => option.companyUserId === value.companyUserId}
      getOptionLabel={(option: VendorUser) => `${option.username} (${option.emailAddress})`}
      renderOption={(props, option: VendorUser) => {
        return (
          <Box component="li" {...props} id={'User_' + option.companyUserId} key={option.companyUserId}>
            {`${option.username} - (${option.emailAddress})`}
          </Box>
        );
      }}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            name="userTypeAhead"
            label="Search Users..."
            margin="normal"
            onKeyDown={(e: any) => {
              e.stopPropagation();
            }}
          />
        </>
      )}
    />
  );
};

export default UserTypeAhead;
